import { motion } from "framer-motion";
import { Ruler, MapPin, GraduationCap, Gift, Moon } from "lucide-react";
import { LOGO, IMAGES } from "@/lib/api";
import { NoisePlayer } from "@/components/NoisePlayer";

const STEPS = [
  { icons: [Ruler, MapPin], color: "#4A7496", bg: "#EAF1F7", title: "Step 1 - Consiglio Personalizzato",
    text: "Inserisci il luogo di nascita e le misure stimate del tuo bimbo per scoprire subito quali taglie e quali tessuti (cotone, ciniglia, caldo cotone) sono adatti a lui." },
  { icons: [GraduationCap], color: "#4E7363", bg: "#EEF4F0", title: "Step 2 - 7 Mini-Lezioni Gratuite",
    text: "Segui le nostre 7 lezioni veloci che ti insegnano passo dopo passo come scegliere i tessuti giusti e capire le taglie senza fare acquisti inutili." },
  { icons: [Gift], color: "#C87D75", bg: "#F9ECE9", title: "Step 3 - 20% di Sconto",
    text: "Non appena completi le 7 lezioni e il percorso, ricevi subito un codice sconto esclusivo del 20% da utilizzare sul sito principale www.minimebaby.it." },
];

const fade = (i) => ({ initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.45, delay: 0.08 * i } });

const StepCard = ({ s, i }) => (
  <motion.div {...fade(i + 4)} className="flex gap-4 p-5 rounded-2xl bg-white border border-[#EFE8E2] shadow-[0_4px_20px_rgba(45,40,37,0.03)]" data-testid={`landing-step-${i + 1}`}>
    <div className="w-12 h-12 shrink-0 rounded-2xl flex items-center justify-center gap-0.5" style={{ background: s.bg, color: s.color }}>
      {s.icons.map((I, k) => <I key={k} size={s.icons.length > 1 ? 17 : 22} strokeWidth={1.7} />)}
    </div>
    <div>
      <h3 className="font-serif text-xl font-semibold text-[#2D2825] leading-snug">{s.title}</h3>
      <p className="text-sm text-[#6E645E] leading-relaxed mt-1">{s.text}</p>
    </div>
  </motion.div>
);

export default function WelcomeTour({ onRegister, onLogin }) {
  return (
    <div className="fixed inset-0 z-[45] bg-[#FAF8F5] overflow-y-auto" data-testid="welcome-landing">
      <div className="max-w-lg mx-auto px-5 pt-6 pb-48">
        <div className="flex items-center justify-between">
          <img src={LOGO} alt="Minime Baby" className="h-9 w-auto object-contain" />
          <button onClick={onLogin} className="text-sm text-[#5C534E] hover:text-[#2D2825]" data-testid="landing-login-top">Accedi</button>
        </div>
        <motion.div {...fade(0)} className="mt-6 rounded-3xl overflow-hidden">
          <img src={IMAGES.hero} alt="Mamma con il corredino Minime" className="w-full h-[200px] sm:h-[260px] object-cover object-[center_32%]" />
        </motion.div>
        <motion.span {...fade(1)} className="mt-6 inline-block text-xs font-semibold tracking-wide px-3.5 py-1.5 rounded-full bg-[#F3ECE6] text-[#8C6B5E] border border-[#E9DED5]" data-testid="landing-badge">
          ✨ L'assistente smart di www.minimebaby.it
        </motion.span>
        <motion.h1 {...fade(2)} className="mt-4 font-serif text-4xl sm:text-5xl text-[#2D2825] font-semibold leading-[1.1] tracking-tight" data-testid="landing-title">
          Non sbagliare taglie e tessuti per il tuo bebè 🤍
        </motion.h1>
        <motion.p {...fade(3)} className="mt-4 text-base text-[#5C534E] leading-relaxed" data-testid="landing-subtitle">
          Inserisci il luogo di nascita e le misure del tuo bambino: riceverai subito il consiglio personalizzato sulle taglie e sui tessuti perfetti per lui. In più, accedi a 7 mini-lezioni pratiche e sblocca il 20% di sconto!
        </motion.p>
        <h2 className="mt-10 text-xs uppercase tracking-[0.2em] font-semibold text-[#8C7F78]">Come funziona</h2>
        <div className="mt-4 grid gap-3" data-testid="landing-steps">
          {STEPS.map((s, i) => <StepCard key={s.title} s={s} i={i} />)}
        </div>
        <div className="mt-6 flex items-center gap-3 rounded-2xl bg-[#F4EFF6] border border-[#E4DAEC] p-3.5" data-testid="landing-sounds">
          <Moon size={18} className="text-[#8E7BA3] shrink-0" />
          <span className="text-sm text-[#5C534E] flex-1">Rumori bianchi e ninne nanne per la nanna: provali subito</span>
          <NoisePlayer label="Prova i suoni" testid="tour-try-sounds" />
        </div>
        <p className="mt-6 text-[11.5px] text-[#8C7F78]">* Sconto del 20% valido su un ordine di almeno 3 capi su www.minimebaby.it</p>
      </div>
      <div className="fixed bottom-0 inset-x-0 z-[46] bg-[#FAF8F5]/90 backdrop-blur-md border-t border-[#EFE8E2]">
        <div className="max-w-lg mx-auto px-5 pt-3 pb-[max(12px,env(safe-area-inset-bottom))] space-y-2">
          <button onClick={onRegister} data-testid="tour-register"
            className="w-full h-14 rounded-full bg-[#2D2825] text-white font-semibold text-base sm:text-lg tracking-wide shadow-[0_10px_30px_-10px_rgba(45,40,37,0.5)] hover:bg-[#45403C] active:scale-[0.98] transition-[background-color,transform]">
            Inizia ora e sblocca il 20% di sconto ✨
          </button>
          <button onClick={onLogin} data-testid="tour-login" className="w-full text-sm text-[#5C534E] hover:text-[#2D2825] underline underline-offset-4 py-1">
            Ho già un account, accedi
          </button>
        </div>
      </div>
    </div>
  );
}
